/**
 * Renders button which acts like select.
 * You have to manage state from outside.
 */
import React, { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { Button } from '@mui/material';
import { PropTypes } from 'prop-types';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export default function SelectButton({
  options,
  value,
  onSelect,
  disabled,
  returnOptionOnSelect,
  label,
  buttonProps = {},
  customTriggerComponent: CustomTriggerComponent,
  menuProps = {},
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClickListItem = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const getOption = (value) => options.find((o) => o.value === value);

  const handleMenuItemClick = (value, label) => {
    const valueToReturn = returnOptionOnSelect ? getOption(value) : value;
    onSelect(valueToReturn, label);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const activeOption = getOption(value);

  return (
    <>
      {CustomTriggerComponent ? (
        <CustomTriggerComponent
          disabled={disabled}
          onClick={handleClickListItem}
          aria-owns={anchorEl ? 'simple-menu' : undefined}
        />
      ) : (
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={disabled}
          aria-owns={anchorEl ? 'simple-menu' : undefined}
          aria-haspopup="true"
          onClick={handleClickListItem}
          sx={{ pr: 4 }}
          {...buttonProps}
        >
          {label ? label : activeOption?.label || activeOption?.value}
          <KeyboardArrowDownIcon sx={{ pl: 1 }} />
        </Button>
      )}

      <Menu
        id="lock-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        {...menuProps}
      >
        {options.map((option, index) => (
          <MenuItem
            key={index}
            disabled={option.disabled}
            selected={option.value === value}
            onClick={() => handleMenuItemClick(option.value, option?.label)}
          >
            {option.label || option.value}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

SelectButton.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // not required, if not provided, value will be used like a label
      disabled: PropTypes.bool,
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  returnOptionOnSelect: PropTypes.bool, // option object iinstead value will be provided to onSelect
};
